'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ArrowDownRight, ArrowUpRight, ChevronDown, ChevronRight,
  CreditCard, Eye, Globe, LayoutGrid, RefreshCw, Sparkles, UserPlus, Users, Zap,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { Avatar } from '@/components/avatar';
import { cn } from '@/lib/utils';

interface AnalyticsResponse {
  range: { days: number; since: string; until: string };
  overview: {
    pageViews: number;
    uniqueVisitors: number;
    sessions: number;
    signups: number;
    signupConversion: number;
    activeUsers: number;
    newUsers: number;
    errors: number;
    revenueCents: number;
    paymentsSucceeded: number;
    paymentsFailed: number;
    appOpens: number;
    appUses: number;
  };
  timeseries: {
    day: string;
    pageViews: number;
    uniqueVisitors: number;
    signups: number;
    errors: number;
    revenueCents: number;
  }[];
  funnel: { step: string; label: string; count: number; dropoffPct: number }[];
  topPages: { path: string; views: number; uniques: number }[];
  topApps: { postId: string; title: string | null; author: string | null; installs: number; opens: number; uses: number; actors: number; eyetimeMs: number }[];
  topReferrers: { host: string; visits: number }[];
  topCountries: { country: string; sessions: number; signups: number }[];
  topErrors: {
    fingerprint: string;
    kind: string;
    count: number;
    firstSeen: string;
    lastSeen: string;
    sampleMessage: string | null;
  }[];
  recentSignups: {
    userId: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    createdAt: string;
    referrerUsername: string | null;
  }[];
  cashflow: {
    daily: { day: string; revenueCents: number; paymentsSucceeded: number; paymentsFailed: number }[];
    byType: { type: string; count: number; totalCents: number }[];
    topCustomers: {
      userId: string;
      username: string | null;
      displayName: string | null;
      totalCents: number;
    }[];
  };
}

const RANGE_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
];

interface CrmIntakeStatus {
  url: string | null;
  folderId: string | null;
  keySet: boolean;
  source: { url: 'db' | 'env' | 'unset'; folderId: 'db' | 'env' | 'unset'; key: 'db' | 'env' | 'unset' };
}

/**
 * Where contacts go, set here rather than in a deploy.
 *
 * The signing key is write-only on purpose: it goes in, and the screen only
 * ever learns whether one is set. Leaving the key box empty keeps the stored
 * one, which is the only sane behaviour for a field that cannot be shown back.
 */
function CrmIntakeCard({ onChange }: { onChange?: (ok: boolean) => void }) {
  const [status, setStatus] = useState<CrmIntakeStatus | null>(null);
  const [url, setUrl] = useState('');
  const [folderId, setFolderId] = useState('');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // A key we hold but never display: the input stays a password box, and this
  // mask is the only thing rendered. Enough to confirm two systems agree on
  // the same secret, never enough to use.
  const [keyMask, setKeyMask] = useState<string | null>(null);

  const apply = useCallback((s: CrmIntakeStatus) => {
    setStatus(s);
    setUrl(s.url ?? '');
    setFolderId(s.folderId ?? '');
    setKey('');
    setKeyMask(null);
    onChange?.(!!(s.url && s.folderId && s.keySet));
  }, [onChange]);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/admin/analytics/crm-intake')
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j) apply(j as CrmIntakeStatus); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [apply]);

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const body: Record<string, string> = { url, folderId };
      // An untouched key box means "leave it alone", not "clear it".
      if (key.trim()) body.key = key;
      const res = await fetch('/api/admin/analytics/crm-intake', {
        method: 'PUT', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) setMsg(json.error || `HTTP ${res.status}`);
      else { apply(json as CrmIntakeStatus); setMsg('Saved.'); }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };

  /** 32 random bytes, base64url — matches what the app's Generate mints. */
  const mintSecret = () => {
    const b = new Uint8Array(32);
    crypto.getRandomValues(b);
    return btoa(String.fromCharCode(...b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };
  const maskOf = (v: string) => `${v.slice(0, 3)}•••• · ${v.length} characters`;
  const hold = (v: string) => { setKey(v); setKeyMask(maskOf(v)); };

  // Pair does the whole handoff server-side: mints the key, writes it into
  // the app's own key slot AND here, and points the address at the app's
  // tokened endpoint. Nothing travels through the browser at all — one
  // click replaces the Generate/Copy/Paste round-trip below, which stays
  // for pushing FROM systems that are not this portal.
  const onPair = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch('/api/admin/analytics/crm-intake/mint', {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({}),
      });
      const json = await res.json();
      if (!res.ok) setMsg(json.hint || json.error || `HTTP ${res.status}`);
      else {
        apply(json.status as CrmIntakeStatus);
        setKeyMask(typeof json.keyMask === 'string' ? json.keyMask : null);
        setMsg('Paired. The key is now set in both places; it was never shown anywhere.');
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not pair.');
    } finally {
      setBusy(false);
    }
  };

  // Generate / Copy / Paste exist so this secret can travel between the portal
  // and the app without ever being displayed — including when an agent is
  // driving the browser, which is the case this is really for.
  const onGenerate = () => { hold(mintSecret()); setMsg('Generated. Copy it, paste it into the app, then Save both.'); };
  const onCopy = async () => {
    if (!key) return;
    try { await navigator.clipboard.writeText(key); setMsg('Copied. Paste it into the app’s intake key, then Save.'); }
    catch { setMsg('The browser did not allow writing to the clipboard here.'); }
  };
  const onPaste = async () => {
    let v = '';
    try { v = (await navigator.clipboard.readText()).trim(); }
    catch { setMsg('The browser did not allow reading the clipboard. Type it into the field instead.'); return; }
    if (!v) { setMsg('The clipboard is empty. Copy the key first.'); return; }
    if (/\s/.test(v) || v.length < 16 || v.length > 512) { setMsg('That does not look like a signing key. Copy it again, then press Paste.'); return; }
    hold(v);
    setMsg('Read from the clipboard. Press Save to store it.');
  };

  const from = (s: 'db' | 'env' | 'unset' | undefined) =>
    s === 'env' ? ' · from the deploy environment' : '';
  const ready = !!(status && status.url && status.folderId && status.keySet);

  return (
    <div className="rounded-xl border border-border p-4 space-y-3">
      <div>
        <p className="text-sm font-medium">Where contacts go</p>
        <p className="text-xs text-muted-foreground">
          The CRM workspace that receives consented contacts and their signals. Saving here overrides
          whatever the deploy environment sets.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Intake address{from(status?.source.url)}</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://robutler.ai/api/agents/bob-xxxxxx-crm/crm-intake"
            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
          />
        </label>
        <label className="text-xs space-y-1">
          <span className="text-muted-foreground">Workspace id{from(status?.source.folderId)}</span>
          <input
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            placeholder="the CRM app's content id"
            className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
          />
        </label>
        <label className="text-xs space-y-1 sm:col-span-2">
          <span className="text-muted-foreground">
            Signing key{from(status?.source.key)}
            {status ? (status.keySet ? ' · set, leave blank to keep it' : ' · not set yet') : ''}
          </span>
          {keyMask ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2 py-1 rounded-md border border-border bg-muted font-mono text-xs">{keyMask}</span>
              <span className="text-xs text-muted-foreground">held, not shown</span>
            </div>
          ) : (
            <input
              value={key}
              onChange={(e) => { setKey(e.target.value); }}
              type="password"
              autoComplete="off"
              placeholder={status?.keySet ? '••••••••  (unchanged)' : "the app's intake_key"}
              className="w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm"
            />
          )}
          <div className="flex items-center gap-2 flex-wrap pt-1">
            <button type="button" onClick={onPair} disabled={busy}
              title="Mint the key and set it here and in the app in one step. It is never shown anywhere."
              className="px-2 py-1 rounded-lg border border-border text-xs font-medium hover:bg-accent disabled:opacity-50">Pair with the app</button>
            <span className="text-xs text-muted-foreground">or by hand:</span>
            <button type="button" onClick={onGenerate}
              title="Make a strong random key. Only a masked preview is shown."
              className="px-2 py-1 rounded-lg border border-border text-xs hover:bg-accent">Generate</button>
            <button type="button" onClick={onCopy} disabled={!key}
              title="Put it on your clipboard so you can paste it into the app. It is never shown."
              className="px-2 py-1 rounded-lg border border-border text-xs hover:bg-accent disabled:opacity-50">Copy</button>
            <button type="button" onClick={onPaste}
              title="Read the key straight from your clipboard. Only a masked preview is shown."
              className="px-2 py-1 rounded-lg border border-border text-xs hover:bg-accent">Paste</button>
            {keyMask ? (
              <button type="button" onClick={() => { setKey(''); setKeyMask(null); }}
                className="px-2 py-1 rounded-lg border border-border text-xs hover:bg-accent">Clear</button>
            ) : null}
          </div>
        </label>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={save}
          disabled={busy}
          className="px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-accent disabled:opacity-50"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <span className="text-xs text-muted-foreground">
          {msg || (ready ? 'Ready to push.' : 'All three are needed before contacts can go anywhere.')}
        </span>
      </div>
    </div>
  );
}

/**
 * The manual CRM backfill trigger (admin verb). One integration surface:
 * consented contacts stream to the CRM app's HMAC-signed intake endpoint.
 * Where that endpoint is comes from the card above. Replaced the pull sync.
 */
function CrmPushCard() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const res = await fetch('/api/admin/analytics/crm-push', { method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}' });
      const json = await res.json();
      if (!res.ok) setResult(json.error || `HTTP ${res.status}`);
      else setResult(`Pushed ${json.pushed} contacts (${json.created} new, ${json.updated} updated) across ${json.pages} page${json.pages === 1 ? '' : 's'}.`);
    } catch (e) {
      setResult(e instanceof Error ? e.message : 'Push failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-border p-4 flex items-center gap-3 flex-wrap">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">Push contacts to CRM</p>
        <p className="text-xs text-muted-foreground">
          Sends every consented contact to the CRM app over its signed intake API. Consent is evaluated per contact at push time; the app re-checks at send.
        </p>
        {result && <p className="text-xs mt-1 text-muted-foreground">{result}</p>}
      </div>
      <button
        onClick={run}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg border border-border text-sm hover:bg-accent disabled:opacity-50"
      >
        {busy ? 'Pushing…' : 'Push now'}
      </button>
    </div>
  );
}

export function AnalyticsTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openSection, setOpenSection] = useState<string | null>('errors');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/admin/analytics?days=${days}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json: AnalyticsResponse) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold">Site analytics</h2>
          <p className="text-xs text-muted-foreground">
            Engagement, funnel, errors, and cashflow across the public portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className={cn(
                  'px-2.5 py-1 text-xs font-medium transition-colors',
                  days === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent text-muted-foreground hover:text-foreground',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setDays((d) => d)}
            className="p-1.5 rounded-lg border border-border hover:bg-accent text-muted-foreground"
            title="Refresh"
            disabled={loading}
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </button>
        </div>
      </header>

      {/* Where before whether: the destination is set here, and the push
          button under it acts on it. */}
      <CrmIntakeCard />
      <CrmPushCard />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="lg" tone="muted" />
        </div>
      ) : error ? (
        <div className="text-sm text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2">
          Failed to load analytics: {error}
        </div>
      ) : data ? (
        <>
          <Kpis data={data} />
          <Timeseries data={data} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Funnel data={data} />
            <Cashflow data={data} />
          </div>
          <TopApps data={data} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopPages data={data} />
            <TopReferrers data={data} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <TopCountries data={data} />
            <RecentSignups data={data} />
          </div>
          <Errors data={data} open={openSection === 'errors'} onToggle={(o) => setOpenSection(o ? 'errors' : null)} />
        </>
      ) : null}
    </div>
  );
}

// ── KPI cards ───────────────────────────────────────────────────────────────

function Kpis({ data }: { data: AnalyticsResponse }) {
  const o = data.overview;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Kpi
        icon={Eye}
        label="Page views"
        value={fmtNum(o.pageViews)}
        sub={`${fmtNum(o.uniqueVisitors)} uniques · ${fmtNum(o.sessions)} sessions`}
      />
      <Kpi
        icon={UserPlus}
        label="Signups"
        value={fmtNum(o.signups)}
        sub={`${fmtPct(o.signupConversion)} conv · ${fmtNum(o.newUsers)} new users`}
      />
      <Kpi
        icon={Users}
        label="Active users"
        value={fmtNum(o.activeUsers)}
        sub="distinct authed actors"
      />
      <Kpi
        icon={CreditCard}
        label="Revenue"
        value={fmtUSD(o.revenueCents)}
        sub={`${fmtNum(o.paymentsSucceeded)} ok · ${fmtNum(o.paymentsFailed)} failed`}
        tone={o.revenueCents > 0 ? 'positive' : undefined}
      />
      <Kpi
        icon={AlertTriangle}
        label="Errors"
        value={fmtNum(o.errors)}
        sub="frontend + react"
        tone={o.errors > 0 ? 'warning' : undefined}
      />
      <Kpi
        icon={Sparkles}
        label="New users"
        value={fmtNum(o.newUsers)}
        sub={`in last ${data.range.days}d`}
      />
      <Kpi
        icon={LayoutGrid}
        label="App usage"
        value={fmtNum(o.appUses)}
        sub={`${fmtNum(o.appOpens)} opens · ${fmtNum(o.appUses)} uses`}
      />
      <Kpi
        icon={Zap}
        label="Funnel: visit → paid"
        value={fmtPct(
          (data.funnel.find((s) => s.step === 'paid')?.count ?? 0) /
            Math.max(1, data.funnel.find((s) => s.step === 'visit')?.count ?? 0),
        )}
        sub={`${fmtNum(data.funnel.find((s) => s.step === 'paid')?.count ?? 0)} paid`}
      />
      <Kpi
        icon={Globe}
        label="Countries"
        value={fmtNum(data.topCountries.length)}
        sub="with traffic"
      />
    </div>
  );
}

// ── Daily timeseries (multi-series bar chart) ───────────────────────────────

function Timeseries({ data }: { data: AnalyticsResponse }) {
  const days = data.timeseries;
  const maxViews = Math.max(1, ...days.map((d) => d.pageViews));
  const maxRev = Math.max(1, ...days.map((d) => d.revenueCents));
  const maxSignups = Math.max(1, ...days.map((d) => d.signups));
  const maxErrors = Math.max(1, ...days.map((d) => d.errors));

  return (
    <Section title="Daily activity">
      <div className="space-y-3">
        <ChartRow label="Page views" tint="bg-primary/70" days={days} accessor={(d) => d.pageViews} max={maxViews} fmt={fmtNum} />
        <ChartRow label="Uniques" tint="bg-primary/40" days={days} accessor={(d) => d.uniqueVisitors} max={maxViews} fmt={fmtNum} />
        <ChartRow label="Signups" tint="bg-emerald-500/70" days={days} accessor={(d) => d.signups} max={maxSignups} fmt={fmtNum} />
        <ChartRow label="Revenue" tint="bg-amber-500/70" days={days} accessor={(d) => d.revenueCents} max={maxRev} fmt={fmtUSD} />
        <ChartRow label="Errors" tint="bg-destructive/70" days={days} accessor={(d) => d.errors} max={maxErrors} fmt={fmtNum} />
      </div>
    </Section>
  );
}

function ChartRow({
  label,
  tint,
  days,
  accessor,
  max,
  fmt,
}: {
  label: string;
  tint: string;
  days: AnalyticsResponse['timeseries'];
  accessor: (d: AnalyticsResponse['timeseries'][number]) => number;
  max: number;
  fmt: (n: number) => string;
}) {
  const total = days.reduce((sum, d) => sum + accessor(d), 0);
  return (
    <div className="grid grid-cols-[100px_1fr_72px] items-center gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <div className="flex items-end gap-[2px] h-12">
        {days.map((d) => {
          const v = accessor(d);
          const h = max > 0 ? Math.max(v > 0 ? 4 : 1, Math.round((v / max) * 100)) : 1;
          return (
            <div
              key={d.day}
              className={cn('flex-1 rounded-t-sm', v > 0 ? tint : 'bg-muted/40')}
              style={{ height: `${h}%` }}
              title={`${d.day} · ${fmt(v)}`}
            />
          );
        })}
      </div>
      <span className="text-right tabular-nums text-muted-foreground">{fmt(total)}</span>
    </div>
  );
}

// ── Funnel ──────────────────────────────────────────────────────────────────

function Funnel({ data }: { data: AnalyticsResponse }) {
  const top = data.funnel[0]?.count ?? 0;
  return (
    <Section title="Acquisition funnel">
      {top === 0 ? (
        <Empty>No traffic in this window.</Empty>
      ) : (
        <div className="space-y-1.5">
          {data.funnel.map((s, i) => {
            const pct = top > 0 ? s.count / top : 0;
            const prev = i === 0 ? null : data.funnel[i - 1];
            // Only show step drop-off when the previous step had signal —
            // otherwise it's noise ("−0%" against an empty upstream).
            const hasUpstream = !!prev && prev.count > 0;
            const stepDrop = hasUpstream ? 1 - s.count / prev!.count : 0;
            const dropLabel =
              i === 0
                ? '—'
                : hasUpstream
                  ? `−${fmtPct(stepDrop)}`
                  : '—';
            return (
              <div key={s.step} className="grid grid-cols-[100px_1fr_auto] items-center gap-3 text-xs">
                <span className="text-muted-foreground">{s.label}</span>
                <div className="relative h-6 rounded bg-muted/40 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/70 flex items-center px-2 text-[10px] tabular-nums text-primary-foreground"
                    style={{ width: `${Math.max(2, Math.round(pct * 100))}%` }}
                  >
                    {pct >= 0.08 ? `${fmtNum(s.count)} · ${fmtPct(pct)}` : ''}
                  </div>
                  {pct < 0.08 && (
                    <span className="absolute inset-y-0 left-2 flex items-center text-[10px] tabular-nums text-foreground/80">
                      {fmtNum(s.count)} · {fmtPct(pct)}
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    'tabular-nums whitespace-nowrap',
                    !hasUpstream && 'text-muted-foreground/60',
                    hasUpstream && stepDrop >= 0.5 && 'text-destructive',
                    hasUpstream && stepDrop < 0.5 && 'text-muted-foreground',
                  )}
                  title={
                    hasUpstream
                      ? `${fmtNum(prev!.count - s.count)} dropped from “${prev!.label}”`
                      : i === 0
                        ? 'Top of funnel'
                        : 'No upstream signal'
                  }
                >
                  {dropLabel}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ── Cashflow ────────────────────────────────────────────────────────────────

function Cashflow({ data }: { data: AnalyticsResponse }) {
  const totalRevenue = data.cashflow.daily.reduce((a, b) => a + b.revenueCents, 0);
  const maxDay = Math.max(1, ...data.cashflow.daily.map((d) => d.revenueCents));
  return (
    <Section title="Cashflow" badge={fmtUSD(totalRevenue)}>
      <div className="space-y-3">
        {/* Daily revenue mini-bars */}
        <div className="flex items-end gap-[2px] h-10">
          {data.cashflow.daily.map((d) => {
            const h = maxDay > 0 ? Math.max(d.revenueCents > 0 ? 4 : 1, Math.round((d.revenueCents / maxDay) * 100)) : 1;
            return (
              <div
                key={d.day}
                className={cn('flex-1 rounded-t-sm', d.revenueCents > 0 ? 'bg-amber-500/70' : 'bg-muted/40')}
                style={{ height: `${h}%` }}
                title={`${d.day} · ${fmtUSD(d.revenueCents)} · ${d.paymentsSucceeded} ok / ${d.paymentsFailed} failed`}
              />
            );
          })}
        </div>

        {data.cashflow.byType.length === 0 ? (
          <Empty>No ledger activity in this window.</Empty>
        ) : (
          <div>
            <h5 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">
              Ledger by type
            </h5>
            <div className="space-y-0.5">
              {data.cashflow.byType.map((r) => {
                const pos = r.totalCents >= 0;
                return (
                  <div key={r.type} className="grid grid-cols-[1fr_auto_auto] items-center gap-3 text-xs">
                    <span className="text-foreground truncate">{prettyTxType(r.type)}</span>
                    <span className="text-muted-foreground tabular-nums">{r.count}×</span>
                    <span className={cn('tabular-nums whitespace-nowrap inline-flex items-center gap-0.5', pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
                      {pos ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                      {fmtUSD(Math.abs(r.totalCents))}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.cashflow.topCustomers.length > 0 && (
          <div>
            <h5 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">
              Top customers
            </h5>
            <div className="space-y-1">
              {data.cashflow.topCustomers.map((c) => (
                <div key={c.userId} className="grid grid-cols-[1fr_auto] items-center gap-3 text-xs">
                  <span className="text-foreground truncate">
                    @{c.username ?? c.userId.slice(0, 8)}
                    {c.displayName && (
                      <span className="text-muted-foreground"> · {c.displayName}</span>
                    )}
                  </span>
                  <span className="tabular-nums text-foreground">{fmtUSD(c.totalCents)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

// ── Top pages ───────────────────────────────────────────────────────────────

// ── Top apps by usage ─────────────────────────────────────────────────────────
function TopApps({ data }: { data: AnalyticsResponse }) {
  const apps = data.topApps;
  const max = Math.max(1, ...apps.map((a) => a.installs + a.opens + a.uses));
  return (
    <Section title="Top apps by usage" badge={`${apps.length}`}>
      {apps.length === 0 ? (
        <Empty>No app usage in this window.</Empty>
      ) : (
        <div className="space-y-1">
          <div className="grid grid-cols-[1fr_52px_52px_52px_64px_52px] gap-3 px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
            <span>App</span>
            <span className="text-right">Installs</span>
            <span className="text-right">Opens</span>
            <span className="text-right">Uses</span>
            <span className="text-right">Eye-time</span>
            <span className="text-right">Actors</span>
          </div>
          {apps.map((a) => {
            const total = a.installs + a.opens + a.uses;
            return (
              <div key={a.postId} className="grid grid-cols-[1fr_52px_52px_52px_64px_52px] items-center gap-3 text-xs">
                <div className="min-w-0">
                  <span className="truncate block text-foreground">{a.title ?? a.postId}</span>
                  <div className="mt-0.5 flex items-center gap-2">
                    <div className="relative h-1.5 flex-1 rounded bg-muted/50 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 bg-primary/70"
                        style={{ width: `${Math.round((total / max) * 100)}%` }}
                      />
                    </div>
                    {a.author && (
                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">@{a.author}</span>
                    )}
                  </div>
                </div>
                <span className="tabular-nums text-right text-muted-foreground">{fmtNum(a.installs)}</span>
                <span className="tabular-nums text-right text-muted-foreground">{fmtNum(a.opens)}</span>
                <span className="tabular-nums text-right text-foreground">{fmtNum(a.uses)}</span>
                <span className="tabular-nums text-right text-foreground">{fmtDuration(a.eyetimeMs)}</span>
                <span className="tabular-nums text-right text-muted-foreground">{fmtNum(a.actors)}</span>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

function TopPages({ data }: { data: AnalyticsResponse }) {
  const max = Math.max(1, ...data.topPages.map((p) => p.views));
  return (
    <Section title="Top pages" badge={`${data.topPages.length}`}>
      {data.topPages.length === 0 ? (
        <Empty>No page views.</Empty>
      ) : (
        <div className="space-y-1">
          {data.topPages.map((p) => (
            <div key={p.path} className="grid grid-cols-[1fr_72px] items-center gap-3 text-xs">
              <div className="min-w-0">
                <span className="truncate block font-mono text-foreground">{p.path}</span>
                <div className="relative h-1.5 rounded bg-muted/50 overflow-hidden mt-0.5">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/70"
                    style={{ width: `${Math.round((p.views / max) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="tabular-nums text-muted-foreground whitespace-nowrap text-right">
                {fmtNum(p.views)}
                <span className="text-[10px] block">{fmtNum(p.uniques)} uniq</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Top referrers ───────────────────────────────────────────────────────────

function TopReferrers({ data }: { data: AnalyticsResponse }) {
  const max = Math.max(1, ...data.topReferrers.map((r) => r.visits));
  return (
    <Section title="Top referrers" badge={`${data.topReferrers.length}`}>
      {data.topReferrers.length === 0 ? (
        <Empty>No referrers tracked.</Empty>
      ) : (
        <div className="space-y-1">
          {data.topReferrers.map((r) => (
            <div key={r.host} className="grid grid-cols-[1fr_auto] items-center gap-3 text-xs">
              <div className="min-w-0">
                <span className="truncate block text-foreground">{r.host}</span>
                <div className="relative h-1.5 rounded bg-muted/50 overflow-hidden mt-0.5">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary/60"
                    style={{ width: `${Math.round((r.visits / max) * 100)}%` }}
                  />
                </div>
              </div>
              <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                {fmtNum(r.visits)}
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Geo ─────────────────────────────────────────────────────────────────────

function TopCountries({ data }: { data: AnalyticsResponse }) {
  const max = Math.max(1, ...data.topCountries.map((c) => c.sessions));
  return (
    <Section title="Geo" badge={`${data.topCountries.length}`}>
      {data.topCountries.length === 0 ? (
        <Empty>No geo data.</Empty>
      ) : (
        <div className="space-y-1">
          {data.topCountries.map((c) => (
            <div key={c.country} className="grid grid-cols-[40px_1fr_auto] items-center gap-3 text-xs">
              <span className="font-mono">{c.country}</span>
              <div className="relative h-4 rounded bg-muted/40 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-emerald-500/60"
                  style={{ width: `${Math.round((c.sessions / max) * 100)}%` }}
                />
              </div>
              <span className="tabular-nums text-muted-foreground whitespace-nowrap">
                {fmtNum(c.sessions)} sess
                {c.signups > 0 && (
                  <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                    · {c.signups} signups
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Recent signups ──────────────────────────────────────────────────────────

function RecentSignups({ data }: { data: AnalyticsResponse }) {
  return (
    <Section title="Recent signups" badge={`${data.recentSignups.length}`}>
      {data.recentSignups.length === 0 ? (
        <Empty>No signups in this window.</Empty>
      ) : (
        <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1">
          {data.recentSignups.map((s) => (
            <div key={s.userId} className="flex items-center gap-2 text-xs">
              <Avatar
                seed={s.username}
                type="human"
                src={s.avatarUrl}
                className="h-6 w-6 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="truncate">
                  <span className="text-foreground">{s.displayName || s.username}</span>
                  <span className="text-muted-foreground"> · @{s.username}</span>
                </p>
                <p className="text-[10px] text-muted-foreground truncate">
                  {new Date(s.createdAt).toLocaleString()}
                  {s.referrerUsername && ` · ref @${s.referrerUsername}`}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ── Errors ──────────────────────────────────────────────────────────────────

function Errors({
  data,
  open,
  onToggle,
}: {
  data: AnalyticsResponse;
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  const ChevronIcon = open ? ChevronDown : ChevronRight;
  const max = Math.max(1, ...data.topErrors.map((e) => e.count));
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => onToggle(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-accent/50 text-left"
      >
        <ChevronIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
        <span className="text-sm font-semibold">Top errors</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {data.topErrors.length} fingerprints · {fmtNum(data.overview.errors)} events
        </span>
      </button>
      {open && (
        <div className="px-3 py-3 border-t border-border">
          {data.topErrors.length === 0 ? (
            <Empty>No frontend errors recorded — clean run.</Empty>
          ) : (
            <div className="space-y-1.5">
              {data.topErrors.map((e) => (
                <div
                  key={`${e.kind}|${e.fingerprint}`}
                  className="grid grid-cols-[1fr_auto] gap-3 items-start text-xs py-1.5 border-b border-border/40 last:border-0"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider',
                          e.kind === 'js_error' && 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
                          e.kind === 'promise_rejection' && 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
                          e.kind === 'react_error' && 'bg-purple-500/15 text-purple-700 dark:text-purple-300',
                        )}
                      >
                        {e.kind}
                      </span>
                      <span className="font-mono text-muted-foreground text-[10px]">
                        {e.fingerprint}
                      </span>
                    </div>
                    {e.sampleMessage && (
                      <p className="text-foreground/90 mt-0.5 break-all line-clamp-2">
                        {e.sampleMessage}
                      </p>
                    )}
                    <div className="relative h-1 rounded bg-muted/40 overflow-hidden mt-1">
                      <div
                        className="absolute inset-y-0 left-0 bg-rose-500/70"
                        style={{ width: `${Math.round((e.count / max) * 100)}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      first {new Date(e.firstSeen).toLocaleString()} · last {new Date(e.lastSeen).toLocaleString()}
                    </p>
                  </div>
                  <span className="tabular-nums text-foreground font-semibold whitespace-nowrap">
                    {fmtNum(e.count)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Shared bits ─────────────────────────────────────────────────────────────

function Kpi({
  icon: Icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  sub?: string;
  tone?: 'positive' | 'warning';
}) {
  return (
    <div className="rounded-lg border border-border bg-background px-3 py-2">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
        <Icon
          className={cn(
            'h-3 w-3',
            tone === 'positive' && 'text-emerald-500',
            tone === 'warning' && 'text-amber-500',
          )}
        />
        {label}
      </div>
      <div className="text-base font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">{sub}</div>}
    </div>
  );
}

function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-sm font-semibold">{title}</h4>
        {badge && (
          <span className="text-[10px] text-muted-foreground tabular-nums">{badge}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground italic">{children}</p>;
}

const NUM_FORMAT = new Intl.NumberFormat('en-US');
function fmtNum(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return NUM_FORMAT.format(Math.round(n));
}

function fmtPct(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '0%';
  if (n < 0.001) return '<0.1%';
  if (n < 0.01) return `${(n * 100).toFixed(1)}%`;
  return `${Math.round(n * 100)}%`;
}

// Foreground eye-time: milliseconds → compact human duration (0s / 45s / 3m / 1.2h).
function fmtDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = s / 60;
  if (m < 60) return `${Math.round(m)}m`;
  const h = m / 60;
  return `${h.toFixed(1)}h`;
}

function fmtUSD(cents: number): string {
  if (!Number.isFinite(cents) || cents === 0) return '$0';
  const dollars = cents / 100;
  if (Math.abs(dollars) >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (Math.abs(dollars) >= 1_000) return `$${(dollars / 1_000).toFixed(1)}k`;
  if (Math.abs(dollars) >= 100) return `$${Math.round(dollars)}`;
  return `$${dollars.toFixed(2)}`;
}

function prettyTxType(t: string): string {
  return t
    .split('_')
    .map((s) => (s.length ? s[0].toUpperCase() + s.slice(1) : s))
    .join(' ');
}

export default AnalyticsTab;
